// Composer — drag overlay, attachment pills, image warning, textarea, send/
// attach/voice/stop buttons, status line. Reads chat state from `chatStore`
// and lifts only refs + the send callback as props so the parent owns
// orchestration.
import { AlertTriangle, ImageIcon, Mic, MicOff, Paperclip, Send, Square, X } from 'lucide-react';
import type { ClipboardEvent, DragEvent, KeyboardEvent, RefObject } from 'react';
import { Button } from '@onestone/ui/button';
import { Textarea } from '@onestone/ui/textarea';
import { chatStore, useChatInput, useChatStore } from '@/lib/chatStore';
import { modelSupportsVision } from '@/lib/modelCapabilities';
import { useAgentName } from '@/lib/useAgentName';
import { useTranscription } from '@/lib/useTranscription';
import { cn } from '@onestone/ui/utils';
import { AttachmentPill } from './AttachmentPill';

interface ChatComposerProps {
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  fileInputRef: RefObject<HTMLInputElement | null>;
  isDragging: boolean;
  isUploading: boolean;
  transcription: ReturnType<typeof useTranscription>;
  onDragEnter: (e: DragEvent) => void;
  onDragLeave: (e: DragEvent) => void;
  onDragOver: (e: DragEvent) => void;
  onDrop: (e: DragEvent) => void;
  onFilePickerChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPaste: (e: ClipboardEvent<HTMLTextAreaElement>) => void;
  onKey: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  onSend: () => void;
}

const ATTACH_ACCEPT =
  'image/*,application/pdf,.txt,.md,.json,.csv,.xml,.html,.css,.js,.jsx,.ts,.tsx,.py,.sh,.yaml,.yml,.toml,.sql,.log,.rs,.go,.java,.c,.cpp,.rb,.php,.vue,.svelte,.graphql,.proto';

export function ChatComposer({
  textareaRef,
  fileInputRef,
  isDragging,
  isUploading,
  transcription,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
  onFilePickerChange,
  onPaste,
  onKey,
  onSend,
}: ChatComposerProps) {
  const { pendingAttachments, uploadError, isStreaming, provider, model } = useChatStore();
  const input = useChatInput();
  const agentName = useAgentName();
  const hasImage = pendingAttachments.some((a) => a.mimeType.startsWith('image/'));
  const visionCapable = hasImage ? modelSupportsVision(model, provider) : null;
  const visionWarning = hasImage && visionCapable === false;

  return (
    <div
      data-testid="chat-composer"
      className={cn('flex h-full flex-col bg-background px-3 py-3 transition-colors', isDragging && 'bg-primary/5')}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {isDragging ? (
        <div className="flex min-h-0 flex-1 items-center justify-center rounded-xl border-2 border-dashed border-primary/40">
          <div className="flex flex-col items-center gap-1 text-primary/70">
            <ImageIcon className="h-6 w-6" />
            <span className="text-xs font-medium">Drop files here — images, docs, code, text</span>
          </div>
        </div>
      ) : (
        <>
          {pendingAttachments.length > 0 && (
            <div className="mb-2 flex shrink-0 flex-wrap gap-1.5">
              {pendingAttachments.map((att) => (
                <AttachmentPill
                  key={att.id}
                  attachment={att}
                  onRemove={() => chatStore.removePendingAttachment(att.id)}
                />
              ))}
            </div>
          )}
          {uploadError && (
            <div className="mb-1.5 flex shrink-0 items-center gap-1.5 rounded-lg bg-destructive/10 px-2.5 py-1.5 text-xs text-destructive">
              <span className="flex-1">{uploadError}</span>
              <button
                type="button"
                className="shrink-0 rounded-sm p-0.5 hover:bg-destructive/20"
                onClick={() => chatStore.setUploadError(null)}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}

          {/* Image warning — capability-aware. Only fires when an image is
              pending AND the catalog/Ollama pattern says the selected model
              can't see images. Unknown models stay silent (the backend will
              gate with a clean error if needed). */}
          {visionWarning && (
            <div className="mb-1.5 flex shrink-0 items-center gap-1.5 rounded-lg bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
              <span>
                {model} doesn&apos;t support image input. Pick a vision-capable model (Claude, Gemini, or an Ollama
                vision model like <span className="font-mono">llava</span>).
              </span>
            </div>
          )}

          <div className="flex min-h-0 flex-1 gap-2">
            <div className="relative min-h-0 flex-1">
              <Textarea
                ref={textareaRef}
                data-testid="chat-input"
                value={input}
                onChange={(e) => chatStore.setInput(e.target.value)}
                onKeyDown={onKey}
                onPaste={onPaste}
                placeholder={`Message ${agentName}…`}
                disabled={isStreaming}
                className="h-full min-h-[3.5rem] resize-none rounded-xl border-border/80 text-sm shadow-sm transition-shadow focus-visible:shadow-md"
              />
            </div>
            <div className="flex shrink-0 flex-col justify-end gap-1.5">
              <input
                ref={fileInputRef}
                type="file"
                accept={ATTACH_ACCEPT}
                multiple
                className="hidden"
                onChange={onFilePickerChange}
              />
              {transcription.isAvailable && (
                <Button
                  type="button"
                  variant={transcription.state === 'recording' ? 'destructive' : 'outline'}
                  size="icon"
                  className={cn('h-7 w-7 rounded-lg', transcription.state === 'recording' && 'animate-pulse')}
                  onClick={() => void transcription.toggle()}
                  disabled={isStreaming || transcription.state === 'transcribing'}
                  title={
                    transcription.state === 'recording'
                      ? 'Stop & transcribe'
                      : transcription.state === 'transcribing'
                        ? 'Transcribing…'
                        : 'Voice input (local Whisper)'
                  }
                  aria-label={
                    transcription.state === 'recording' ? 'Stop recording and transcribe' : 'Start voice input'
                  }
                >
                  {transcription.state === 'transcribing' ? (
                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  ) : transcription.state === 'recording' ? (
                    <MicOff className="h-3.5 w-3.5" aria-hidden="true" />
                  ) : (
                    <Mic className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-7 w-7 rounded-lg"
                onClick={() => fileInputRef.current?.click()}
                disabled={isStreaming || isUploading}
                title="Attach files"
                aria-label="Attach files"
              >
                {isUploading ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : (
                  <Paperclip className="h-3.5 w-3.5" aria-hidden="true" />
                )}
              </Button>
              {isStreaming ? (
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="h-7 w-7 rounded-lg"
                  onClick={() => chatStore.stopStreaming()}
                  title="Stop streaming (Esc)"
                  aria-label="Stop streaming response"
                  data-testid="chat-stop-btn"
                >
                  <Square className="h-3.5 w-3.5" aria-hidden="true" />
                </Button>
              ) : (
                <Button
                  type="button"
                  size="icon"
                  className="h-7 w-7 rounded-lg"
                  onClick={onSend}
                  disabled={input.trim().length === 0 && pendingAttachments.length === 0}
                  title="Send (Enter or Ctrl+Enter)"
                  aria-label="Send message"
                  data-testid="chat-send-btn"
                >
                  <Send className="h-3.5 w-3.5" aria-hidden="true" />
                </Button>
              )}
            </div>
          </div>
          <div className="mt-1.5 flex shrink-0 justify-between text-xs text-muted-foreground">
            <span>
              {transcription.state === 'recording'
                ? 'Recording — click mic to transcribe'
                : transcription.state === 'transcribing'
                  ? 'Transcribing locally with Whisper…'
                  : transcription.error
                    ? `Voice: ${transcription.error}`
                    : isStreaming
                      ? 'Streaming — Esc to stop'
                      : 'Enter to send · Shift+Enter for new line · Paste or drop files'}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
